// webAdbTap.js — run the Android logcat tap from the dashboard over WebUSB
// (Chrome/Edge desktop), no adb install or repo checkout needed. Talks to the
// phone with ya-webadb, parses with logcatTap.js, and posts rows to the same
// POST /proxy/ingest/rows the CLI uses (proxyAuth's fetch wrapper adds the
// bearer token). The adb library is dynamically imported so it only loads
// when someone actually connects a phone.

import { createLogcatTap, BASE_AD_HOSTS } from './logcatTap';
import { md5 } from './md5';

export function webUsbSupported() {
  return typeof navigator !== 'undefined' && !!navigator.usb && window.isSecureContext;
}

// Turn library/browser errors into something an operator can act on.
// `step` is where the USB work failed: 'open' (Chrome could not open the
// device), 'claim' (opened, but another program holds the adb interface),
// 'running' (dropped mid-capture). Every NetworkError used to read as "held
// by another program", which sent people hunting for an adb that wasn't there.
export function explainError(e, step = e && e.usbStep) {
  const msg = String((e && e.message) || e || '');
  // Chrome's own wording, so a wrong guess below is visible on screen.
  const chrome = String((e && e.cause && e.cause.message) || msg);
  const said = chrome && !/already in used by another program/i.test(chrome) ? ` (Chrome: ${chrome})` : '';
  const network = e && (e.name === 'NetworkError' || (e.cause && e.cause.name === 'NetworkError'));
  if (network && step === 'open') {
    return `Chrome could not open the phone. Another app has the whole USB device (Android File Transfer, OpenMTP, Smart Switch, Vysor) or the connection is stale: set the phone's USB mode to "No data transfer", unplug and replug, then connect again.${said}`;
  }
  if (network && step === 'running') {
    return `The USB connection dropped during capture (cable moved, or the phone slept or switched USB mode). Plug it back in and connect again.${said}`;
  }
  if (/already in used by another program|Unable to claim interface|claimInterface/i.test(msg) || network) {
    return `The phone is held by another program. Quickest fix: unplug the phone, plug it back in, and connect again. If it keeps happening, quit what grabs it: an adb server (\`adb kill-server\` in a terminal, or Force Quit "adb" in Activity Monitor; \`npm run tap\`, Android Studio and scrcpy restart it), Android File Transfer, or another AdLens tab or browser.${said}`;
  }
  if (/No device selected|NotFoundError/i.test(msg) || (e && e.name === 'NotFoundError')) {
    return 'No phone was selected.';
  }
  if (/SecurityError|Access denied/i.test(msg) || (e && e.name === 'SecurityError')) {
    return 'The browser blocked USB access. Use Chrome or Edge over https (or localhost) and allow the device prompt.';
  }
  return msg || 'Connection failed';
}

// The USB device of a connect still in progress (picker done, waiting on
// the phone's Allow prompt), so Cancel can free it.
let pending = null;

async function releaseUsb(raw) {
  try { if (raw && raw.opened) await raw.close(); } catch { /* already gone */ }
}

/** Abort an in-progress connect: closing the device fails the pending handshake. */
export async function abortPendingConnect() {
  const raw = pending;
  pending = null;
  await releaseUsb(raw);
}

// android_id (16 hex, from `settings`) and the GAID (a UUID, Google Play
// services) are different IDs. No shell command reads the GAID on an unrooted
// phone, so take it from the `rdid` the GMA SDK puts on GAM requests: it is
// the value the app actually sends, and the one to register as a GAM test device.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function adIdFromUrl(url) {
  let q;
  try { q = new URL(url).searchParams; } catch { return null; }
  const id = q.get('rdid');
  // A row cut at logcat's ~4KB limit can end mid-value; only a whole UUID counts.
  return id && UUID.test(id) ? { gaid: id.toLowerCase(), lat: q.get('is_lat') === '1' } : null;
}

// The ID for RequestConfiguration.setTestDeviceIds on Android is
// MD5(android_id), uppercase hex. The SDK also logs it under the Ads tag on
// a non-test device; that logged value wins when seen, since it is the SDK's own.
export const testDeviceIdFor = (androidId) => (androidId ? md5(androidId).toUpperCase() : null);
const TEST_ID_LOG = /setTestDeviceIds\(Arrays\.asList\("([0-9A-F]{32})"\)\)/i;
export function testDeviceIdFromLog(text) {
  const m = TEST_ID_LOG.exec(text);
  return m ? m[1].toUpperCase() : null;
}

async function readAndroidId(adb) {
  try {
    const v = (await adb.subprocess.noneProtocol.spawnWaitText(['settings', 'get', 'secure', 'android_id'])).trim();
    return /^[0-9a-f]+$/i.test(v) ? v : null;
  } catch { return null; }
}

async function fetchHosts(apiBase) {
  try {
    const r = await fetch(`${apiBase}/proxy/ingest/hosts`);
    const j = await r.json();
    if (Array.isArray(j.hosts) && j.hosts.length) return j.hosts;
  } catch { /* older proxy: fall back */ }
  return BASE_AD_HOSTS;
}

/**
 * startWebUsbTap({ apiBase, deviceName, onStatus, onStats }) -> { stop(), device }
 * onStatus(phase, detail): 'selecting' | 'authorizing' | 'running' | 'stopped' | 'error'
 * onStats({ seen, deduped, sent, rejected, lastAt, gaid, lat, loggedTestId }) every flush.
 * Must be called from a click handler (requestDevice needs a user gesture).
 */
export async function startWebUsbTap({ apiBase, deviceName, onStatus = () => {}, onStats = () => {} }) {
  const [{ Adb, AdbDaemonTransport }, { AdbDaemonWebUsbDeviceManager }, credMod] = await Promise.all([
    import('@yume-chan/adb'),
    import('@yume-chan/adb-daemon-webusb'),
    import('@yume-chan/adb-credential-web'),
  ]);
  const AdbWebCredentialStore = credMod.default;
  const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
  if (!manager) throw new Error('WebUSB is not available in this browser. Use Chrome or Edge on a desktop.');

  onStatus('selecting');
  const device = await manager.requestDevice();
  if (!device) throw new Error('No device selected');

  // A device this tab opened earlier and never released (a failed or
  // cancelled attempt) would make claimInterface fail with "busy" against
  // ourselves. Start from a closed device.
  await releaseUsb(device.raw);

  let adb = null;
  let proc;
  let hosts;
  let androidId = null;
  const name = deviceName || `android-${device.serial}`;
  pending = device.raw;
  try {
    let connection;
    try {
      connection = await device.connect();
    } catch (e) {
      // connect() opens then claims: still closed means the open failed.
      try { e.usbStep = device.raw && device.raw.opened ? 'claim' : 'open'; } catch { /* frozen error */ }
      throw e;
    }
    // First connect shows "Allow USB debugging?" on the phone; the key is kept in
    // this browser's IndexedDB so later connects don't ask again.
    onStatus('authorizing', 'Unlock the phone and tap Allow on the USB debugging prompt.');
    const transport = await AdbDaemonTransport.authenticate({
      serial: device.serial,
      connection,
      credentialStore: new AdbWebCredentialStore('AdLens'),
    });
    adb = new Adb(transport);
    hosts = await fetchHosts(apiBase);
    androidId = await readAndroidId(adb);
    await adb.subprocess.noneProtocol.spawnWaitText(['setprop', 'log.tag.Ads', 'VERBOSE']);
    proc = await adb.subprocess.noneProtocol.spawn(['logcat', '-s', 'Ads', '-v', 'time']);
  } catch (e) {
    // Never leave the phone claimed by this tab after a failed connect.
    if (adb) { try { await adb.close(); } catch { /* ignore */ } }
    await releaseUsb(device.raw);
    throw e;
  } finally {
    pending = null;
  }

  const tap = createLogcatTap({ deviceName: name, hosts });
  const totals = { seen: 0, deduped: 0, sent: 0, rejected: 0, lastAt: null, gaid: null, lat: false, loggedTestId: null };
  let stopped = false;

  async function flush() {
    const rows = tap.take();
    totals.seen = tap.stats.seen;
    totals.deduped = tap.stats.deduped;
    for (const r of rows) {
      const id = adIdFromUrl(r.url);
      if (id) { totals.gaid = id.gaid; totals.lat = id.lat; }
    }
    for (let i = 0; i < rows.length; i += 500) {
      const chunk = rows.slice(i, i + 500);
      try {
        const r = await fetch(`${apiBase}/proxy/ingest/rows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: chunk, source: 'logcat' }),
        });
        const j = await r.json().catch(() => ({}));
        if (r.ok) { totals.sent += j.ingested || 0; totals.rejected += j.rejected || 0; totals.lastAt = Date.now(); }
        else totals.rejected += chunk.length;
      } catch { totals.rejected += chunk.length; }
    }
    onStats({ ...totals });
  }
  const timer = setInterval(flush, 2000);

  const decoder = new TextDecoder();
  const reader = proc.output.getReader();
  let logTail = '';
  (async () => {
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = decoder.decode(value, { stream: true });
        tap.feed(text);
        if (!totals.loggedTestId) {
          // Keep a short tail so a log line split across two chunks still matches.
          logTail = (logTail + text).slice(-600);
          const id = testDeviceIdFromLog(logTail);
          if (id) totals.loggedTestId = id;
        }
      }
    } catch (e) {
      if (!stopped) onStatus('error', explainError(e, 'running'));
    } finally {
      tap.end();
      clearInterval(timer);
      await flush();
      if (!stopped) { stopped = true; onStatus('stopped', 'The phone disconnected or logcat ended.'); }
      try { await adb.close(); } catch { /* already gone */ }
    }
  })();

  onStatus('running');
  return {
    device: { serial: device.serial, name: device.name, deviceName: name, androidId, testDeviceId: testDeviceIdFor(androidId) },
    async stop() {
      if (stopped) return;
      stopped = true;
      try { await proc.kill(); } catch { /* ignore */ }
      try { reader.cancel(); } catch { /* ignore */ }
      clearInterval(timer);
      await flush();
      try { await adb.close(); } catch { /* ignore */ }
      onStatus('stopped');
    },
  };
}
